import {
  WithdrawalOfferDto,
  WithdrawalTransactionDto,
} from '../../../openapi-ts';

export type WithdrawalFlowType =
  | 'none'
  | 'no-offers'
  | 'external-redirect'
  | 'inline-form';

export type WithdrawalOffer = WithdrawalOfferDto;

export interface WithdrawalViewState {
  flowType: WithdrawalFlowType;
  activeBankGateway: string | null;
  showBankSelector: boolean;
  showNoOffersInfo: boolean;
  showLoggedInApplicationSelector: boolean;
  showExternalRedirect: boolean;
  showInlineForm: boolean;
}

export interface WithdrawalTransactionOption {
  withdrawalProductId: string;
  transactionId: string;
  label: string;
  confirmationLabel: string;
  bankGateway?: string;
}

export interface PendingWithdrawal {
  withdrawalProductId: string;
  transactionId: string;
  label: string;
}

export interface WithdrawalConfirmationData {
  c24Id: string;
  email: string;
  date: string;
  time: string;
  status: string;
}

export const ANONYMOUS_OTHER_BANKS = 'OTHERS';
export const WITHDRAWAL_PRODUCT_KREDIT = 'KREDIT';
export const WITHDRAWAL_PRODUCT_EKS = 'EKS';

export function isEksProduct(product: string): boolean {
  return product === WITHDRAWAL_PRODUCT_EKS;
}

/** Banks that redirect to an external withdrawal form. URL is the only runtime config. */
export const EXTERNAL_BANK_REDIRECT_URLS: Record<string, string> = {
  TARGOBANK: 'https://www.targobank.de/widerruf/check24AT/',
};

export const BANK_SELECTOR_OPTIONS: Array<{
  value: string;
  label: string;
  disabled?: boolean;
}> = [
  { value: '', label: 'Wählen Sie eine Bank aus', disabled: true },
  { value: 'TARGOBANK', label: 'Targobank' },
  { value: ANONYMOUS_OTHER_BANKS, label: 'andere Banken' },
];

export function getBankDisplayName(bankGateway: string): string {
  return (
    BANK_SELECTOR_OPTIONS.find((option) => option.value === bankGateway)
      ?.label ?? bankGateway
  );
}

export function getOfferTransactions(
  offer: WithdrawalOfferDto | null | undefined,
): WithdrawalTransactionDto[] {
  return offer?.transactionsWithLabels ?? [];
}

function listTransactionsForProduct(
  offers: WithdrawalOfferDto[],
  product: string,
): WithdrawalTransactionDto[] {
  return offers
    .flatMap((offer) => getOfferTransactions(offer))
    .filter((transaction) => transaction.product === product);
}

export function getTransactionForProduct(
  offer: WithdrawalOfferDto | null | undefined,
  product: string,
): WithdrawalTransactionDto | null {
  return (
    getOfferTransactions(offer).find(
      (transaction) => transaction.product === product,
    ) ?? null
  );
}

export function hasTransactionForProduct(
  offers: WithdrawalOfferDto[],
  product: string,
): boolean {
  return listTransactionsForProduct(offers, product).length > 0;
}

export function hasAnyTransactions(offers: WithdrawalOfferDto[]): boolean {
  return offers.some((offer) => getOfferTransactions(offer).length > 0);
}

export function toPendingWithdrawal(
  transaction: WithdrawalTransactionDto | null | undefined,
): PendingWithdrawal | null {
  if (!transaction?.withdrawalProductId || !transaction.transactionId) {
    return null;
  }

  return {
    withdrawalProductId: transaction.withdrawalProductId,
    transactionId: transaction.transactionId,
    label: transaction.label ?? '',
  };
}

export function isWithdrawalTransactionOption(
  value: unknown,
): value is WithdrawalTransactionOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    'withdrawalProductId' in value &&
    'transactionId' in value
  );
}

export function isSameTransactionOption(
  a: WithdrawalTransactionOption,
  b: WithdrawalTransactionOption,
): boolean {
  return (
    a.withdrawalProductId === b.withdrawalProductId &&
    a.transactionId === b.transactionId
  );
}

export function getTransactionOptionsForProduct(
  offers: WithdrawalOfferDto[],
  product: string,
): WithdrawalTransactionOption[] {
  return listTransactionsForProduct(offers, product)
    .filter(
      (transaction) =>
        transaction.withdrawalProductId &&
        transaction.transactionId &&
        transaction.label,
    )
    .map((transaction) => ({
      withdrawalProductId: transaction.withdrawalProductId!,
      transactionId: transaction.transactionId!,
      label:
        product === WITHDRAWAL_PRODUCT_EKS
          ? transaction.label!
          : `${transaction.bankGateway} - ${transaction.withdrawalProductId} - ${transaction.label}`,
      confirmationLabel: transaction.label ?? '',
      bankGateway: transaction.bankGateway,
    }));
}

export function resolveWithdrawalFlowType(
  bankGateway: string | null,
): WithdrawalFlowType {
  if (!bankGateway) {
    return 'none';
  }

  if (bankGateway in EXTERNAL_BANK_REDIRECT_URLS) {
    return 'external-redirect';
  }

  return 'inline-form';
}
